import React, { useRef, useState, useEffect, useCallback } from "react";
import { Camera, RefreshCw, AlertCircle, Aperture } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { getFaceLandmarker, solveOverlay } from "@/lib/faceLandmarker";

const PUPIL_BIAS = 0.08; // pupils sit slightly above the vertical centre of a lens

export function FaceTryOnCamera({ glassesUrl, product, children }) {
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [faceDetected, setFaceDetected] = useState(false);
  const [fitScale, setFitScale] = useState(1);
  const [fitY, setFitY] = useState(0);
  const videoRef = useRef(null);
  const boxRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const landmarkerRef = useRef(null);
  const fitRef = useRef({ scale: 1, y: 0 });
  const productRef = useRef(product);

  useEffect(() => { fitRef.current = { scale: fitScale, y: fitY }; }, [fitScale, fitY]);
  useEffect(() => { productRef.current = product; }, [product]);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const detectLoop = useCallback(() => {
    const video = videoRef.current;
    const box = boxRef.current;
    const overlay = overlayRef.current;
    if (!video || !box || !overlay || !landmarkerRef.current || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(detectLoop);
      return;
    }
    const result = landmarkerRef.current.detectForVideo(video, performance.now());
    const landmarks = result?.faceLandmarks?.[0];
    if (landmarks) {
      setFaceDetected(true);
      const placement = solveOverlay(
        landmarks,
        { w: video.videoWidth, h: video.videoHeight },
        { w: box.clientWidth, h: box.clientHeight },
        productRef.current,
        { scale: fitRef.current.scale, offsetY: fitRef.current.y },
      );
      const aspect = overlay.naturalHeight && overlay.naturalWidth ? overlay.naturalHeight / overlay.naturalWidth : 0.4;
      overlay.style.width = `${placement.width}px`;
      overlay.style.left = `${placement.x}px`;
      overlay.style.top = `${placement.y + placement.width * aspect * PUPIL_BIAS}px`;
      overlay.style.transform = `translate(-50%, -50%) rotate(${placement.angle}deg)`;
      overlay.style.opacity = "1";
    } else {
      setFaceDetected(false);
      overlay.style.opacity = "0";
    }
    rafRef.current = requestAnimationFrame(detectLoop);
  }, []);

  const startCamera = async () => {
    setStatus("loading");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 960 }, height: { ideal: 720 } } });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      try {
        if (!landmarkerRef.current) landmarkerRef.current = await getFaceLandmarker("VIDEO");
      } catch (_modelErr) {
        stopCamera();
        setStatus("error");
        setErrorMsg("Couldn't start face tracking on this device. Try the Upload Photo mode instead.");
        return;
      }
      setStatus("active");
      rafRef.current = requestAnimationFrame(detectLoop);
    } catch (err) {
      stopCamera();
      setStatus("error");
      setErrorMsg(err?.name === "NotAllowedError" ? "Camera access was denied. Allow camera permission and try again." : "Couldn't start the camera on this device/browser.");
    }
  };

  const restart = () => {
    stopCamera();
    setStatus("idle");
    setFaceDetected(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    const box = boxRef.current;
    if (!video || !box) return;
    const w = box.clientWidth;
    const h = box.clientHeight;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    const videoAspect = video.videoWidth / video.videoHeight;
    const boxAspect = w / h;
    let dw, dh, dx, dy;
    if (videoAspect > boxAspect) {
      dh = h; dw = h * videoAspect; dx = (w - dw) / 2; dy = 0;
    } else {
      dw = w; dh = w / videoAspect; dx = 0; dy = (h - dh) / 2;
    }
    ctx.drawImage(video, dx, dy, dw, dh);
    if (overlay && overlay.style.opacity !== "0" && overlay.complete) {
      const oLeft = parseFloat(overlay.style.left) || 0;
      const oTop = parseFloat(overlay.style.top) || 0;
      const oWidth = parseFloat(overlay.style.width) || 0;
      const oHeight = oWidth * (overlay.naturalHeight / overlay.naturalWidth || 0.4);
      const rotMatch = /rotate\(([-\d.]+)deg\)/.exec(overlay.style.transform || "");
      const rotDeg = rotMatch ? parseFloat(rotMatch[1]) : 0;
      ctx.save();
      ctx.translate(oLeft, oTop);
      ctx.rotate((rotDeg * Math.PI) / 180);
      ctx.drawImage(overlay, -oWidth / 2, -oHeight / 2, oWidth, oHeight);
      ctx.restore();
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "lookers-try-on.png";
    a.click();
    toast.success("Try-on saved to your device");
  };

  return (
    <div>
      <div
        ref={boxRef}
        className="relative w-full aspect-[3/4] max-w-md mx-auto bg-zinc-900 border border-zinc-200 rounded-2xl overflow-hidden"
        data-testid="face-tryon-camera-box"
      >
        <div className="absolute inset-0 scale-x-[-1]">
          <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="face-tryon-video" />
          <img
            ref={overlayRef}
            src={glassesUrl}
            alt="Glasses overlay"
            crossOrigin="anonymous"
            className="absolute pointer-events-none opacity-0 transition-opacity duration-150"
            style={{ willChange: "transform, width, left, top" }}
            data-testid="face-tryon-overlay-image"
          />
        </div>

        {status !== "active" && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/85 text-center px-6">
            {status === "idle" && (
              <button onClick={startCamera} className="flex flex-col items-center gap-3 text-white" data-testid="face-tryon-start-camera-button">
                <span className="w-14 h-14 rounded-full bg-teal-600 flex items-center justify-center"><Camera size={22} /></span>
                <span className="font-heading text-xs uppercase tracking-wide">Turn On Camera</span>
                <span className="text-[11px] text-zinc-300 max-w-[220px]">The frames track your face automatically, sized to their real measurements.</span>
              </button>
            )}
            {status === "loading" && <p className="text-white text-xs font-heading uppercase tracking-wide">Starting camera…</p>}
            {status === "error" && (
              <div className="flex flex-col items-center gap-3 text-white max-w-[240px]">
                <AlertCircle size={22} className="text-amber-400" />
                <p className="text-xs" data-testid="face-tryon-error-message">{errorMsg}</p>
                <button onClick={startCamera} className="text-xs underline" data-testid="face-tryon-retry-button">Try again</button>
              </div>
            )}
          </div>
        )}

        {status === "active" && !faceDetected && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-zinc-900/80 text-white text-[11px] font-heading uppercase tracking-wide px-3 py-1.5 rounded-full" data-testid="face-tryon-no-face-hint">
            Centre your face in frame
          </div>
        )}
      </div>

      {children}

      {status === "active" && (
        <div className="max-w-md mx-auto mt-6 space-y-4" data-testid="face-tryon-fit-controls">
          <div className="text-center">
            <button onClick={capturePhoto} className="inline-flex items-center gap-2 bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide px-6 py-3 rounded-full hover:bg-teal-600 transition-colors" data-testid="face-tryon-capture-button">
              <Aperture size={14} /> Capture This Look
            </button>
          </div>
          <div>
            <div className="flex justify-between text-[11px] font-heading uppercase tracking-wide text-zinc-500 mb-1.5">
              <span>Fine-Tune Size</span>
            </div>
            <Slider value={[fitScale]} min={0.8} max={1.25} step={0.01} onValueChange={([v]) => setFitScale(v)} data-testid="face-tryon-scale-slider" />
          </div>
          <div>
            <div className="flex justify-between text-[11px] font-heading uppercase tracking-wide text-zinc-500 mb-1.5">
              <span>Vertical Position</span>
            </div>
            <Slider value={[fitY]} min={-40} max={40} step={1} onValueChange={([v]) => setFitY(v)} data-testid="face-tryon-vertical-slider" />
          </div>
          <div className="text-center">
            <button onClick={restart} className="inline-flex items-center gap-1.5 text-xs underline text-zinc-500" data-testid="face-tryon-stop-camera-button">
              <RefreshCw size={12} /> Turn off camera
            </button>
          </div>
        </div>
      )}
      <p className="text-center text-xs text-zinc-400 mt-3">Your camera feed stays on your device and is never recorded or uploaded.</p>
    </div>
  );
}
